import mixpanel from 'mixpanel-browser';

// Environment check
const isDevelopment = process.env.NODE_ENV === 'development';

// Mixpanel configuration
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || 'test-token-development';

// Consent management
const CONSENT_KEY = 'mixpanel_analytics_consent';
const CONSENT_VERSION = '1.0';

type ConsentStatus = 'granted' | 'denied' | 'pending';

interface ConsentData {
  status: ConsentStatus;
  timestamp: string;
  version: string;
}

interface EventProperties {
  [key: string]: any;
}

interface UserProperties {
  [key: string]: any;
}

// Event routing configuration
type TrackingMode = 'client_only' | 'server_only' | 'both' | 'fallback';

interface EventConfig {
  mode: TrackingMode;
  description: string;
}

// Define which events go where to avoid duplicates
const EVENT_ROUTING: Record<string, EventConfig> = {
  // Client-only: UX and behavioral metrics
  'page_viewed': { mode: 'client_only', description: 'Navigation patterns' },
  'sidebar_opened': { mode: 'client_only', description: 'UI interactions' },
  'sidebar_closed': { mode: 'client_only', description: 'UI interactions' },
  'modal_opened': { mode: 'client_only', description: 'UI interactions' },
  'modal_closed': { mode: 'client_only', description: 'UI interactions' },
  'button_clicked': { mode: 'client_only', description: 'UI interactions' },
  'scroll_depth': { mode: 'client_only', description: 'Content engagement' },
  'mouse_hover': { mode: 'client_only', description: 'User behavior' },
  'performance_metric': { mode: 'client_only', description: 'Technical metrics' },
  
  // Server-only: Reliable business metrics
  'search_performed': { mode: 'server_only', description: 'Core feature usage' },
  'user_logged_in': { mode: 'server_only', description: 'Authentication events' },
  'user_logged_out': { mode: 'server_only', description: 'Authentication events' },
  'concept_clicked': { mode: 'server_only', description: 'Engagement metrics' },
  'topic_processed': { mode: 'server_only', description: 'Content processing' },
  'search_results_viewed': { mode: 'server_only', description: 'Search engagement' },
  'exploration_loaded': { mode: 'server_only', description: 'History usage' },
  'recommendation_clicked': { mode: 'server_only', description: 'AI recommendations' },
  'session_ended': { mode: 'server_only', description: 'Session metrics' },
  
  // Both: Critical events we can't afford to lose (marked with source)
  'user_signed_up': { mode: 'both', description: 'Critical conversion event' },
  'consent_decision': { mode: 'both', description: 'Privacy compliance' },
  
  // Fallback: Try client first, server if blocked
  'app_initialized': { mode: 'fallback', description: 'App lifecycle' },
  'graph_viewed': { mode: 'fallback', description: 'Feature usage' },
  'leaderboard_viewed': { mode: 'fallback', description: 'Feature usage' },
  
  // Activity tracking events (server-only for reliable time tracking)
  'session_started': { mode: 'server_only', description: 'Session lifecycle' },
  'session_paused': { mode: 'server_only', description: 'Session lifecycle' },
  'session_resumed': { mode: 'server_only', description: 'Session lifecycle' },
  'activity_heartbeat': { mode: 'server_only', description: 'Active time tracking' },
  'manual_activity': { mode: 'server_only', description: 'Manual activity triggers' }
};

class MixpanelService {
  private initialized = false;
  private debugMode = isDevelopment;

  /**
   * Check if user has Do Not Track enabled
   */
  private isDNTEnabled(): boolean {
    // Check various DNT signals
    return (
      navigator.doNotTrack === '1' ||
      navigator.doNotTrack === 'yes' ||
      // @ts-ignore - some browsers use msDoNotTrack
      navigator.msDoNotTrack === '1' ||
      // @ts-ignore - window.doNotTrack is deprecated but still check
      window.doNotTrack === '1'
    );
  }

  /**
   * Get current consent status
   */
  getConsentStatus(): ConsentData | null {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) return null;
      
      const data: ConsentData = JSON.parse(stored);
      
      // Check if consent version is current
      if (data.version !== CONSENT_VERSION) {
        return null; // Require new consent for version changes
      }
      
      return data;
    } catch (error) {
      console.error('[Mixpanel] Failed to get consent status:', error);
      return null;
    }
  }

  /**
   * Set consent status
   */
  setConsentStatus(status: ConsentStatus): void {
    try {
      const consentData: ConsentData = {
        status,
        timestamp: new Date().toISOString(),
        version: CONSENT_VERSION
      };
      
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
      
      if (this.debugMode) {
        console.log('[Mixpanel] Consent status set:', status);
      }

      // Apply consent immediately if Mixpanel is initialized
      if (this.initialized) {
        this.applyConsentSettings();
      }
    } catch (error) {
      console.error('[Mixpanel] Failed to set consent status:', error);
    }
  }

  /**
   * Check if tracking is allowed based on consent and DNT
   */
  private canTrack(): boolean {
    // Check DNT first - always respect it
    if (this.isDNTEnabled()) {
      if (this.debugMode) {
        console.log('[Mixpanel] Tracking blocked by Do Not Track');
      }
      return false;
    }

    // Check consent
    const consent = this.getConsentStatus();
    if (!consent || consent.status !== 'granted') {
      if (this.debugMode) {
        console.log('[Mixpanel] Tracking blocked by consent status:', consent?.status || 'none');
      }
      return false;
    }

    return true;
  }

  /**
   * Apply current consent settings to Mixpanel
   */
  private applyConsentSettings(): void {
    if (!this.initialized) return;

    try {
      if (this.canTrack()) {
        mixpanel.opt_in_tracking();
        if (this.debugMode) {
          console.log('[Mixpanel] Tracking enabled (consent + no DNT)');
        }
      } else {
        mixpanel.opt_out_tracking();
        if (this.debugMode) {
          console.log('[Mixpanel] Tracking disabled');
        }
      }
    } catch (error) {
      console.error('[Mixpanel] Failed to apply consent settings:', error);
    }
  }

  /**
   * Initialize Mixpanel with configuration
   */
  init(): void {
    if (this.initialized) {
      console.warn('[Mixpanel] Already initialized');
      return;
    }

    try {
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: this.debugMode,
        persistence: 'localStorage',
        // Always start with tracking disabled - we'll enable based on consent/DNT
        opt_out_tracking_by_default: true,
        // Ignore DNT in the SDK - we'll handle it ourselves for better control
        ignore_dnt: true
      });

      this.initialized = true;
      
      if (this.debugMode) {
        console.log('[Mixpanel] Initialized in development mode with token:', MIXPANEL_TOKEN);
        console.log('[Mixpanel] DNT Status:', this.isDNTEnabled() ? 'ENABLED' : 'DISABLED');
        console.log('[Mixpanel] Consent Status:', this.getConsentStatus()?.status || 'NONE');
      }

      // Apply current consent settings
      this.applyConsentSettings();
    } catch (error) {
      console.error('[Mixpanel] Initialization failed:', error);
    }
  }

  /**
   * Get event routing configuration
   */
  private getEventConfig(eventName: string): EventConfig {
    return EVENT_ROUTING[eventName] || { mode: 'fallback', description: 'Unknown event' };
  }

  /**
   * Track event server-side (immune to client-side blocking)
   */
  private async trackServerSide(eventName: string, properties?: EventProperties): Promise<void> {
    try {
      const eventData = {
        event: eventName,
        properties: {
          ...properties,
          timestamp: new Date().toISOString(),
          environment: process.env.NODE_ENV,
          tracking_source: 'server'
        }
      };

      if (this.debugMode) {
        console.log('[Mixpanel] Server-side event:', eventData);
      }

      // Server-side analytics endpoint (immune to client-side blocking)
      const response = await fetch('http://localhost:8000/api/analytics/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      if (this.debugMode) {
        console.log('[Mixpanel] Server-side tracking successful:', eventName);
      }
    } catch (error) {
      if (this.debugMode) {
        console.error('[Mixpanel] Server-side tracking failed:', error);
      }
      // Don't throw - we don't want to break the app if analytics fails
    }
  }

  /**
   * Track event client-side (subject to consent/DNT)
   */
  private trackClientSide(eventName: string, properties?: EventProperties): void {
    if (!this.canTrack()) {
      if (this.debugMode) {
        console.log('[Mixpanel] Client-side tracking blocked:', eventName);
      }
      return;
    }

    try {
      const eventData = {
        event: eventName,
        properties: {
          ...properties,
          timestamp: new Date().toISOString(),
          environment: process.env.NODE_ENV,
          tracking_source: 'client'
        }
      };

      if (this.debugMode) {
        console.log('[Mixpanel] Client-side event:', eventData);
      }

      mixpanel.track(eventName, eventData.properties);
    } catch (error) {
      console.error('[Mixpanel] Client-side tracking failed:', error);
    }
  }

  /**
   * Track an event using smart routing to avoid duplicates
   */
  track(eventName: string, properties?: EventProperties): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized, skipping event:', eventName);
      return;
    }

    const config = this.getEventConfig(eventName);

    if (this.debugMode) {
      console.log(`[Mixpanel] Routing event "${eventName}" via ${config.mode} (${config.description})`);
    }

    switch (config.mode) {
      case 'client_only':
        this.trackClientSide(eventName, properties);
        break;

      case 'server_only':
        this.trackServerSide(eventName, properties);
        break;

      case 'both':
        // Track on both client and server with source markers
        this.trackClientSide(eventName, properties);
        this.trackServerSide(eventName, properties);
        break;

      case 'fallback':
        // Try client first, fallback to server if blocked
        if (this.canTrack()) {
          this.trackClientSide(eventName, properties);
        } else {
          this.trackServerSide(eventName, properties);
        }
        break;

      default:
        console.warn('[Mixpanel] Unknown tracking mode for event:', eventName);
    }
  }

  /**
   * Identify user server-side (immune to client-side blocking)
   */
  private async identifyServerSide(userId: string, properties?: UserProperties): Promise<void> {
    try {
      const userData = {
        user_id: userId,
        ...properties
      };

      if (this.debugMode) {
        console.log('[Mixpanel] Server-side identify:', userData);
      }

      // Server-side identification endpoint (immune to client-side blocking)
      const response = await fetch('http://localhost:8000/api/analytics/identify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      if (this.debugMode) {
        console.log('[Mixpanel] Server-side identification successful:', userId);
      }
    } catch (error) {
      if (this.debugMode) {
        console.error('[Mixpanel] Server-side identification failed:', error);
      }
    }
  }

  /**
   * Identify user client-side (subject to consent/DNT)
   */
  private identifyClientSide(userId: string): void {
    if (!this.canTrack()) {
      if (this.debugMode) {
        console.log('[Mixpanel] Client-side identification blocked:', userId);
      }
      return;
    }

    try {
      if (this.debugMode) {
        console.log('[Mixpanel] Client-side identifying user:', userId);
      }

      mixpanel.identify(userId);
    } catch (error) {
      console.error('[Mixpanel] Client-side identification failed:', error);
    }
  }

  /**
   * Identify a user using hybrid approach
   */
  identify(userId: string, properties?: UserProperties): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized, skipping identify:', userId);
      return;
    }

    if (this.debugMode) {
      console.log('[Mixpanel] Identifying user:', userId, 'with properties:', properties);
    }

    // Use hybrid approach: try client-side, always do server-side
    this.identifyClientSide(userId);
    this.identifyServerSide(userId, properties);
  }

  /**
   * Set user properties client-side
   */
  private setUserPropertiesClientSide(properties: UserProperties): void {
    if (!this.canTrack()) {
      if (this.debugMode) {
        console.log('[Mixpanel] Client-side user properties blocked');
      }
      return;
    }

    try {
      if (this.debugMode) {
        console.log('[Mixpanel] Setting client-side user properties:', properties);
      }

      mixpanel.people.set(properties);
    } catch (error) {
      console.error('[Mixpanel] Setting client-side user properties failed:', error);
    }
  }

  /**
   * Set user properties using hybrid approach
   */
  setUserProperties(userId: string, properties: UserProperties): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized, skipping user properties');
      return;
    }

    if (this.debugMode) {
      console.log('[Mixpanel] Setting user properties for:', userId, properties);
    }

    // Client-side properties (subject to blocking)
    this.setUserPropertiesClientSide(properties);
    
    // Server-side properties (via identify endpoint with properties)
    this.identifyServerSide(userId, properties);
  }

  /**
   * Grant consent for tracking
   */
  grantConsent(): void {
    this.setConsentStatus('granted');
  }

  /**
   * Deny consent for tracking
   */
  denyConsent(): void {
    this.setConsentStatus('denied');
  }

  /**
   * Check if tracking is enabled
   */
  isTracking(): boolean {
    if (!this.initialized) {
      return false;
    }

    try {
      return !mixpanel.has_opted_out_tracking();
    } catch (error) {
      console.error('[Mixpanel] Failed to check tracking status:', error);
      return false;
    }
  }

  /**
   * Reset user data (for logout)
   */
  reset(): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized, cannot reset');
      return;
    }

    try {
      if (this.debugMode) {
        console.log('[Mixpanel] Resetting user data');
      }

      mixpanel.reset();
    } catch (error) {
      console.error('[Mixpanel] Reset failed:', error);
    }
  }

  /**
   * Get event routing information (for debugging)
   */
  getEventRouting(): Record<string, EventConfig> {
    return EVENT_ROUTING;
  }

  /**
   * Get event config for a specific event (for debugging)
   */
  getEventInfo(eventName: string): EventConfig {
    return this.getEventConfig(eventName);
  }
}

// Export singleton instance
export const mixpanelService = new MixpanelService();
// Phase 2 complete - DNT and consent handling

// For development testing - expose service globally
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  // @ts-ignore
  window.mixpanelService = mixpanelService;
  console.log('[Mixpanel] Development mode - use window.mixpanelService to test in console');
}

// Export types for use in other files
export type { EventProperties, UserProperties, ConsentStatus, ConsentData, TrackingMode, EventConfig };